import { Schema } from "effect";
import { normalizeBridgeUrlInput } from "../../lib/connection-settings-normalization";
import { isSettingsPinFormValue } from "../../lib/settings-pin-input";

/**
 * Language type
 */
const LanguageSchema = Schema.Literals(["de", "en"]);
export type Language = Schema.Schema.Type<typeof LanguageSchema>;

// The forbidden default PIN rule lives in `lib/pin-crypto` so that every
// platform adapter enforces the identical rule at the gate (ITSiRL §75b SGB V).
import { isDefaultPin } from "../../lib/pin-crypto";
import { isPlausibleKimAddress } from "../../lib/kim-address";


/**
 * URL validation helper
 */
const isValidUrl = (url: string): boolean => {
  const normalizedUrl = normalizeBridgeUrlInput(url);
  if (!URL.canParse(normalizedUrl)) {
    return false;
  }

  const protocol = new URL(normalizedUrl).protocol;
  return protocol === "https:" || protocol === "http:";
};

/**
 * Settings form schema
 */
export const SettingsFormSchema = Schema.Struct({
  // Practice settings
  bridgeUrl: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1, { message: "validation.required" })),
    Schema.check(Schema.makeFilter((url) => isValidUrl(url), {
      message: "validation.invalid-url",
    })),
  ),
  solToken: Schema.optional(Schema.String),
  practiceName: Schema.String,
  welcomeMessage: Schema.String,
  // Terminal settings
  selectedTerminalId: Schema.optional(Schema.String),
  // Device identification for bridge dashboard
  deviceName: Schema.optional(Schema.String),
  // KIM address override (uses bridge default if empty). This address feeds
  // the PKV Online-Check-in QR, so an implausible value must never be saved.
  kimAddressOverride: Schema.optional(
    Schema.String.pipe(
      Schema.check(Schema.makeFilter((value) => value.trim().length === 0 || isPlausibleKimAddress(value), { message: "validation.invalid-kim-address" })),
    ),
  ),
  // Feature availability settings
  schedulerAvailableInSelfService: Schema.optional(Schema.Boolean),
  prescriptionServiceAvailableInSelfService: Schema.optional(Schema.Boolean),
  // Digitale Anamnese — post-check-in link/QR to an external anamnesis provider
  anamnesisEnabled: Schema.optional(Schema.Boolean),
  anamnesisUrl: Schema.optional(Schema.String),
  // Styling settings
  pin: Schema.String.pipe(
    Schema.check(Schema.isMinLength(4, { message: "validation.pin-too-short" })),
    Schema.check(Schema.makeFilter(isSettingsPinFormValue, { message: "settings.security.pin-hint" })),
    // ITSiRL §75b SGB V: the default PIN 0000 must be actively rejected.
    Schema.check(Schema.makeFilter((pin) => !isDefaultPin(pin), {
      message: "validation.pin-default-rejected",
    })),
  ),
  voiceOverVolume: Schema.Number.pipe(Schema.check(Schema.isInt()), Schema.check(Schema.isBetween({ minimum: 0, maximum: 100 }))),
  defaultKioskLanguage: Schema.optional(Schema.String),
  languageSelectionOnSessionStart: Schema.optional(Schema.Boolean),
});

export type SettingsFormData = Schema.Schema.Type<typeof SettingsFormSchema>;
